import { supabase } from '../supabaseClient';

const UNCATEGORIZED_CODE = 'uncategorized';

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const numberValue = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(numberValue) ? numberValue : null;
}

function normalizeText(value) {
  return String(value || '').trim();
}

async function getCurrentUserId() {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!data?.user?.id) throw new Error('로그인이 만료되었습니다. 다시 로그인 후 이용해주세요.');
  return data.user.id;
}

function normalizeContractPayload(payload) {
  return {
    insurance_company: normalizeText(payload.insurance_company),
    product_name: normalizeText(payload.product_name),
    joined_at: payload.joined_at || null,
    monthly_premium: toNumberOrNull(payload.monthly_premium),
    payment_period: normalizeText(payload.payment_period),
    coverage_period: normalizeText(payload.coverage_period),
    renewal_type: normalizeText(payload.renewal_type) || '확인필요',
    contract_status: normalizeText(payload.contract_status) || '유지중',
    contractor: normalizeText(payload.contractor),
    insured: normalizeText(payload.insured),
    policy_number: normalizeText(payload.policy_number) || null,
    memo: normalizeText(payload.memo) || null,
  };
}

function normalizeCoveragePayload(payload) {
  return {
    standard_coverage_id: payload.standard_coverage_id || null,
    original_name: normalizeText(payload.original_name),
    coverage_amount: toNumberOrNull(payload.coverage_amount),
    coverage_period: normalizeText(payload.coverage_period),
    payment_period: normalizeText(payload.payment_period),
    is_renewable: typeof payload.is_renewable === 'boolean' ? payload.is_renewable : null,
    memo: normalizeText(payload.memo) || null,
  };
}

function getCoverageCategory(coverage) {
  return coverage.standard_coverage_categories || coverage.category || null;
}

function getCoverageGroupKey(coverage) {
  const category = getCoverageCategory(coverage);
  if (category?.id) return `standard:${category.id}`;
  return `uncategorized:${normalizeText(coverage.original_name) || coverage.id}`;
}

function getCoverageLabel(coverage) {
  const category = getCoverageCategory(coverage);
  if (category?.name) return category.name;
  return normalizeText(coverage.original_name) || '기타/미분류';
}

function getAggregationMode(coverage) {
  const category = getCoverageCategory(coverage);
  return category?.aggregation_mode || 'review_required';
}

function getStatusLabel(mode) {
  if (mode === 'sum') return '합산';
  if (mode === 'separate') return '별도';
  return '확인 필요';
}

function getCriteriaCategory(item) {
  return item.standard_coverage_categories || item.category || null;
}

function toComparableAmount(value) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function makeAnalysisStatus({ mode, currentAmount, targetAmount, hasSummary }) {
  if (mode === 'separate') return '별도';
  if (mode === 'review_required') return '확인 필요';
  if (mode !== 'sum') return '확인 필요';
  if (targetAmount === null) return '확인 필요';
  if (!hasSummary || currentAmount <= 0) return '미가입';
  if (currentAmount < targetAmount) return '부족';
  if (currentAmount > targetAmount) return '기준 초과';
  return '충족';
}

const customerInsuranceService = {
  async listMasterData() {
    const [categoriesResult, companiesResult] = await Promise.all([
      supabase
        .from('standard_coverage_categories')
        .select('id, code, name, group_name, aggregation_mode, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true }),
      supabase
        .from('insurance_company_options')
        .select('id, name, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true }),
    ]);

    if (categoriesResult.error) throw categoriesResult.error;
    if (companiesResult.error) throw companiesResult.error;

    return {
      categories: categoriesResult.data || [],
      companies: companiesResult.data || [],
    };
  },

  async listContracts(customerId) {
    if (!customerId) return [];

    const { data, error } = await supabase
      .from('customer_insurance_contracts')
      .select(`
        *,
        coverages:customer_insurance_coverages (
          *,
          standard_coverage_categories (
            id,
            code,
            name,
            group_name,
            aggregation_mode
          )
        )
      `)
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((contract) => ({
      ...contract,
      coverages: [...(contract.coverages || [])].sort(
        (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      ),
    }));
  },

  async listCustomerInsuranceOverview(customerIds) {
    const ids = (customerIds || []).filter(Boolean);
    if (ids.length === 0) return {};

    const { data, error } = await supabase
      .from('customer_insurance_contracts')
      .select(`
        id,
        customer_id,
        updated_at,
        coverages:customer_insurance_coverages (
          id,
          updated_at
        )
      `)
      .in('customer_id', ids);

    if (error) throw error;

    return (data || []).reduce((map, contract) => {
      const key = contract.customer_id;
      const current = map[key] || {
        contractCount: 0,
        coverageCount: 0,
        latestUpdatedAt: '',
      };

      current.contractCount += 1;
      current.coverageCount += contract.coverages?.length || 0;

      const dates = [
        current.latestUpdatedAt,
        contract.updated_at,
        ...(contract.coverages || []).map((coverage) => coverage.updated_at),
      ].filter(Boolean);

      current.latestUpdatedAt = dates.sort().at(-1) || '';
      map[key] = current;
      return map;
    }, {});
  },

  async createContract(customerId, payload) {
    const userId = await getCurrentUserId();
    const nextPayload = normalizeContractPayload(payload);

    const { data, error } = await supabase
      .from('customer_insurance_contracts')
      .insert({
        ...nextPayload,
        user_id: userId,
        customer_id: customerId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateContract(contractId, payload) {
    const userId = await getCurrentUserId();
    const { data, error } = await supabase
      .from('customer_insurance_contracts')
      .update(normalizeContractPayload(payload))
      .eq('id', contractId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async removeContract(contractId) {
    const userId = await getCurrentUserId();
    const { error } = await supabase
      .from('customer_insurance_contracts')
      .delete()
      .eq('id', contractId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  async createCoverage(customerId, contractId, payload) {
    const userId = await getCurrentUserId();
    const nextPayload = normalizeCoveragePayload(payload);

    const { data, error } = await supabase
      .from('customer_insurance_coverages')
      .insert({
        ...nextPayload,
        user_id: userId,
        customer_id: customerId,
        contract_id: contractId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateCoverage(coverageId, payload) {
    const userId = await getCurrentUserId();
    const { data, error } = await supabase
      .from('customer_insurance_coverages')
      .update(normalizeCoveragePayload(payload))
      .eq('id', coverageId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async removeCoverage(coverageId) {
    const userId = await getCurrentUserId();
    const { error } = await supabase
      .from('customer_insurance_coverages')
      .delete()
      .eq('id', coverageId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  calculateCoverageSummary(contracts) {
    const grouped = new Map();

    (contracts || []).forEach((contract) => {
      (contract.coverages || []).forEach((coverage) => {
        const key = getCoverageGroupKey(coverage);
        const category = getCoverageCategory(coverage);
        const mode = getAggregationMode(coverage);

        if (!grouped.has(key)) {
          grouped.set(key, {
            key,
            standardCoverageId: category?.id || null,
            standardCoverageCode: category?.code || null,
            name: getCoverageLabel(coverage),
            groupName: category?.group_name || '기타',
            aggregationMode: mode,
            statusLabel: getStatusLabel(mode),
            totalAmount: 0,
            contractIds: new Set(),
            details: [],
          });
        }

        const item = grouped.get(key);
        item.contractIds.add(contract.id);

        if (Number.isFinite(Number(coverage.coverage_amount))) {
          item.totalAmount += Number(coverage.coverage_amount);
        }

        item.details.push({
          id: coverage.id,
          insuranceCompany: contract.insurance_company || '보험사 미입력',
          productName: contract.product_name || '상품명 미입력',
          originalName: coverage.original_name || item.name,
          amount: coverage.coverage_amount,
          coveragePeriod: coverage.coverage_period,
          paymentPeriod: coverage.payment_period,
          isRenewable: coverage.is_renewable,
          memo: coverage.memo,
        });
      });
    });

    return Array.from(grouped.values())
      .map((item) => ({
        ...item,
        contractCount: item.contractIds.size,
        contractIds: undefined,
      }))
      .sort((a, b) => {
        if (a.statusLabel !== b.statusLabel) return a.statusLabel.localeCompare(b.statusLabel, 'ko');
        return a.name.localeCompare(b.name, 'ko');
      });
  },

  calculateCoverageAnalysis(summary, criteriaItems) {
    const summaryByStandardId = new Map();
    const includedSummaryKeys = new Set();

    (summary || []).forEach((item) => {
      if (item.standardCoverageId) {
        summaryByStandardId.set(item.standardCoverageId, item);
      }
    });

    const rows = (criteriaItems || [])
      .filter((item) => item.is_enabled)
      .map((item) => {
        const category = getCriteriaCategory(item);
        const mode = category?.aggregation_mode || 'review_required';
        const summaryItem = category?.id ? summaryByStandardId.get(category.id) : null;
        const currentAmount = toComparableAmount(summaryItem?.totalAmount) || 0;
        const targetAmount = mode === 'sum' ? toComparableAmount(item.target_amount) : null;
        const difference = mode === 'sum' && targetAmount !== null ? currentAmount - targetAmount : null;

        if (summaryItem?.key) includedSummaryKeys.add(summaryItem.key);

        return {
          key: `criteria:${item.id || category?.id}`,
          standardCoverageId: category?.id || null,
          name: category?.name || '기타/미분류',
          groupName: category?.group_name || '기타',
          aggregationMode: mode,
          currentAmount,
          targetAmount,
          difference,
          status: makeAnalysisStatus({
            mode,
            currentAmount,
            targetAmount,
            hasSummary: Boolean(summaryItem),
          }),
          displayOrder: Number(item.display_order ?? category?.sort_order ?? 1000),
          memo: item.memo,
          summary: summaryItem || null,
          details: summaryItem?.details || [],
        };
      });

    (summary || []).forEach((item) => {
      if (includedSummaryKeys.has(item.key)) return;

      const mode = item.aggregationMode || 'review_required';
      rows.push({
        key: `summary:${item.key}`,
        standardCoverageId: item.standardCoverageId || null,
        name: item.name || '기타/미분류',
        groupName: item.groupName || '기타',
        aggregationMode: mode,
        currentAmount: toComparableAmount(item.totalAmount) || 0,
        targetAmount: null,
        difference: null,
        status: mode === 'separate' ? '별도' : '확인 필요',
        displayOrder: 10000,
        memo: '분석기준에 포함되지 않은 등록 담보입니다.',
        summary: item,
        details: item.details || [],
      });
    });

    return rows.sort((a, b) => {
      if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
      return a.name.localeCompare(b.name, 'ko');
    });
  },

  getUncategorizedCategory(categories) {
    return (categories || []).find((category) => category.code === UNCATEGORIZED_CODE) || null;
  },
};

export default customerInsuranceService;
